/* eslint-disable jsx-a11y/label-has-associated-control */

import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "components/ui/Button";
import { Form, FormField, FormMessage } from "components/ui/Form";
import { TextArea } from "components/ui/Input";
import classNames from "components/ui/classNames";
import SendFeedbackMutation from "lib/graphql/mutations/SendFeedbackMutation";
import type {
  ISendFeedbackMutation,
  ISendFeedbackMutationVariables,
} from "lib/graphql/mutations/__generated__/SendFeedbackMutation.generated";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import type { IntlShape } from "react-intl";
import { FormattedMessage, useIntl } from "react-intl";
import { z } from "zod";

type FeedbackFormValues = z.infer<ReturnType<typeof feedbackFormDataShape>>;

interface FeedbackFormProps {
  defaultTitle: string | null;
  className?: string;
  onSubmit?: (values: FeedbackFormValues) => void;
}

export default function FeedbackForm({ defaultTitle, className, onSubmit }: FeedbackFormProps) {
  const intl = useIntl();
  const form = useForm<FeedbackFormValues>({
    mode: "onSubmit",
    resolver: zodResolver(feedbackFormDataShape(intl)),
    defaultValues: {
      message: "",
    },
  });

  const [sendFeedback, { loading }] = useMutation<ISendFeedbackMutation, ISendFeedbackMutationVariables>(
    SendFeedbackMutation,
  );

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(async (values) => {
          await toast.promise(
            sendFeedback({
              variables: {
                input: {
                  body: values.message,
                  title: defaultTitle?.trim() || null,
                  rating: null,
                },
              },
            }),
            {
              loading: intl.formatMessage({
                defaultMessage: "Enviando feedback...",
                id: "R7XSaK",
              }),
              success: () => {
                form.reset();
                return intl.formatMessage({
                  defaultMessage: "Feedback enviado. Obrigado!",
                  id: "hVuFrD",
                });
              },
              error: intl.formatMessage({
                defaultMessage: "Erro ao enviar feedback. Tente novamente mais tarde.",
                id: "dRXfAP",
              }),
            },
          );

          onSubmit?.(values);
        })}
        autoComplete="off"
        className={classNames("my-6 flex flex-col gap-y-4", className)}
      >
        {defaultTitle && (
          <h2
            className={classNames(
              "rounded-xl bg-slate-100 px-3 py-2 text-xl font-bold text-slate-800",
              "dark:bg-slate-900/40 dark:text-slate-300",
            )}
          >
            {defaultTitle}
          </h2>
        )}

        <label htmlFor="message" className="flex cursor-pointer flex-col space-y-2">
          <strong className="font-bold dark:text-slate-300">
            <FormattedMessage defaultMessage="Mensagem" id="n2A8jB" />
          </strong>

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => {
              return (
                <div className="space-y-2">
                  <TextArea
                    {...field}
                    id="message"
                    autoFocus={true}
                    maxLength={1200}
                    rows={8}
                    autoComplete="off"
                    placeholder={intl.formatMessage({
                      defaultMessage: "Escreva uma mensagem de feedback...",
                      id: "tY7Vx0",
                    })}
                    className="resize-none rounded-xl text-base"
                  />

                  <FormMessage />
                </div>
              );
            }}
          />
        </label>

        <Button disabled={loading} shape="capsule" variant="primary/teal" type="submit">
          {loading ? (
            <FormattedMessage defaultMessage="Aguarde..." id="VZazm7" />
          ) : (
            <FormattedMessage defaultMessage="Enviar feedback" id="ESX6ed" />
          )}
        </Button>

        <FormMessage asRootError={true} />
      </form>
    </Form>
  );
}

function feedbackFormDataShape(intl: IntlShape) {
  return z.object({
    message: z
      .string()
      .trim()
      .min(
        10,
        intl.formatMessage({
          defaultMessage: "Sua mensagem deve ter pelo menos 10 caracteres",
          id: "snKdcv",
        }),
      )
      .max(
        1200,
        intl.formatMessage({
          defaultMessage: "Sua mensagem deve ter no máximo 1200 caracteres",
          id: "rDHX01",
        }),
      ),
  });
}
